import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isLinkNode } from "@lexical/link";
import { $findMatchingParent } from "@lexical/utils";
import { $getNearestNodeFromDOMNode, $getNodeByKey } from "lexical";
import { Check, ExternalLink, Pencil, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isSafeUrl } from "components/helpers";

// Delay before the card hides, so the pointer can cross the gap from the link
// into the card without it disappearing first.
const CLOSE_DELAY_MS = 150;

/**
 * Resolve the link under a hovered anchor element.
 *
 * Returns the node key rather than the node, so callers re-read the node at
 * action time: an edit elsewhere in the document between the hover and the
 * click must not make Save or Remove act on a stale node. An href that fails
 * the scheme check resolves to null, so an unsafe stored URL produces no card
 * at all.
 */
function resolveLinkAtAnchor(editor, anchor) {
  if (!anchor || !editor.getRootElement()?.contains(anchor)) return null;
  return editor.read(() => {
    const node = $getNearestNodeFromDOMNode(anchor);
    if (!node) return null;
    const link = $isLinkNode(node)
      ? node
      : $findMatchingParent(node, $isLinkNode);
    if (!link) return null;
    const url = link.getURL();
    return isSafeUrl(url) ? { key: link.getKey(), url } : null;
  });
}

function LinkHoverPlugin() {
  const [editor] = useLexicalComposerContext();
  // The hovered link, kept visible through the grace delay so the pointer can
  // reach the card. Null when no card is shown.
  const [card, setCard] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editUrl, setEditUrl] = useState("");
  const [invalid, setInvalid] = useState(false);
  const closeTimer = useRef(null);
  // scheduleClose is memoized, so its closure would capture a stale `editing`.
  const editingRef = useRef(false);

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      // Leaving the card mid-edit must not discard the in-progress URL.
      if (editingRef.current) return;
      setCard(null);
    }, CLOSE_DELAY_MS);
  }, [cancelClose]);

  const closeNow = useCallback(() => {
    cancelClose();
    setCard(null);
    setEditing(false);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    // Emit only on anchor changes, so moving across the text inside one link
    // does not churn state or reset an in-progress edit.
    let lastAnchor = null;

    const handleMouseOver = event => {
      const anchor = event.target.closest?.("a");
      if (!anchor || anchor === lastAnchor) return;
      const resolved = resolveLinkAtAnchor(editor, anchor);
      if (!resolved) return;
      lastAnchor = anchor;
      cancelClose();
      setCard({
        key: resolved.key,
        url: resolved.url,
        rect: anchor.getBoundingClientRect(),
      });
      setEditing(false);
    };

    const handleMouseOut = event => {
      const anchor = event.target.closest?.("a");
      if (!anchor || anchor !== lastAnchor) return;
      // Moving to another node still inside the same anchor is not an exit.
      if (event.relatedTarget && anchor.contains(event.relatedTarget)) return;
      lastAnchor = null;
      scheduleClose();
    };

    return editor.registerRootListener((rootElement, prevRootElement) => {
      prevRootElement?.removeEventListener("mouseover", handleMouseOver);
      prevRootElement?.removeEventListener("mouseout", handleMouseOut);
      rootElement?.addEventListener("mouseover", handleMouseOver);
      rootElement?.addEventListener("mouseout", handleMouseOut);
    });
  }, [editor, cancelClose, scheduleClose]);

  const startEditing = () => {
    const url = editor.read(() => $getNodeByKey(card.key)?.getURL() ?? null);
    if (url == null) {
      closeNow();
      return;
    }
    setEditUrl(url);
    setInvalid(false);
    setEditing(true);
  };

  const handleSave = event => {
    event.preventDefault();
    // The card is portaled into the note form's React tree, so without this the
    // submit would bubble to the form's onSubmit and save the whole note.
    event.stopPropagation();
    const url = editUrl.trim();
    if (!isSafeUrl(url)) {
      setInvalid(true);
      return;
    }
    editor.update(() => {
      const link = $getNodeByKey(card.key);
      if ($isLinkNode(link)) link.setURL(url);
    });
    closeNow();
  };

  // Unwrapping the node leaves the text in place, rather than acting on the
  // selection, which the pointer has not moved.
  const handleRemove = () => {
    editor.update(() => {
      const link = $getNodeByKey(card.key);
      if (!$isLinkNode(link)) return;
      for (const child of link.getChildren()) {
        link.insertBefore(child);
      }
      link.remove();
    });
    closeNow();
  };

  if (!card) return null;

  // Portaled under document.body for the same reason as the mention popup: a
  // hosting dialog applies a CSS transform that would otherwise become the
  // containing block for this fixed-position card and misplace it.
  return createPortal(
    <div
      data-link-hover-card=""
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      className="tw:fixed tw:z-[1100] tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface-overlay)] tw:px-2 tw:py-1 tw:shadow-lg"
      style={{ left: card.rect.left, top: card.rect.bottom + 4 }}
    >
      {editing ? (
        <form onSubmit={handleSave} className="tw:flex tw:flex-col tw:gap-1.5">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Input
              autoFocus
              value={editUrl}
              onChange={event => {
                setEditUrl(event.target.value);
                setInvalid(false);
              }}
              placeholder="https://"
              aria-invalid={invalid}
              aria-label="Link URL"
              className="tw:h-7 tw:w-56 tw:text-sm"
            />
            <Button type="submit" size="icon-sm" title="Save link">
              <Check className="tw:size-4" />
            </Button>
          </div>
          {invalid && (
            <p className="tw:text-xs tw:text-destructive">
              Enter an http, https, mailto or tel URL.
            </p>
          )}
        </form>
      ) : (
        <>
          <span
            className="tw:max-w-56 tw:truncate tw:text-sm tw:text-[var(--color-text-secondary)]"
            title={card.url}
          >
            {card.url}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            render={
              <a href={card.url} target="_blank" rel="noopener noreferrer" />
            }
            title="Open in new tab"
          >
            <ExternalLink className="tw:size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={startEditing}
            title="Edit link"
          >
            <Pencil className="tw:size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleRemove}
            title="Remove link"
          >
            <X className="tw:size-4" />
          </Button>
        </>
      )}
    </div>,
    document.body
  );
}

export default LinkHoverPlugin;
